import { tokens } from "lib/colors"
import React, { useContext, useEffect, useState } from "react"
import Text from "lib/components/Text"
import { LanguageContext } from "context/LanguageContext"
import { useAuth } from "lib/auth"
import { useRouter } from "next/router"
import fetcher from "lib/fetcher"
import getSafeRedirectPath from "lib/safe-redirect"
import { toast } from "react-toastify"
import { captureException } from "@sentry/react"
import { useForm } from "react-hook-form"
import InputPassword from "lib/components/InputPassword"
import Icon from "lib/components/Icons"
import { hasUppercase, hasLowercase, containsNumbers } from "lib/helpers"
import Button from "lib/components/Button"
import safeLocalStorage from "lib/storage"

interface ModalChangeAccountPasswordProps {
  isOpen: boolean
}

function ModalChangeAccountPassword({ isOpen }: ModalChangeAccountPasswordProps) {
  const router = useRouter()
  const { useTranslation: t } = useContext(LanguageContext)
  const { userProfile, handleUserProfile } = useAuth()

  const [loadingButton, setLoadingButton] = useState<boolean>(false)

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    watch,
    formState: { errors },
  } = useForm()

  const watchPassword = watch("password")
  const watchConfirmPassword = watch("confirmPassword")

  useEffect(() => {
    if (watchConfirmPassword && watchConfirmPassword.length > 0) {
      if (watchConfirmPassword !== watchPassword) {
        setError("confirmPassword", { message: t("the_confirmation_passwords_is_match") })
      } else {
        clearErrors("confirmPassword")
      }
    }
  }, [watchPassword, watchConfirmPassword, setError, clearErrors, t])

  const submitForm = async (data) => {
    const { password } = data
    setLoadingButton(true)
    try {
      await fetcher("/api/auth/update-new-password", "POST", {
        new_password: password,
      })

      let message = t("login_successful_with_username")
      const dataEmail = userProfile?.email.includes("@")
      if (dataEmail) {
        message = t("login_successful_with_email")
      }
      toast.success(message)
      setLoadingButton(false)
      setTimeout(() => {
        window.location.replace(getSafeRedirectPath(router.query.r))
      }, 1000)
      safeLocalStorage.removeItem("is_login_before")
      await handleUserProfile()
    } catch (err) {
      let messages = err.message
      if (err.message === "ErrUserAlreadyExist") {
        messages = t("that_email_is_taken")
        setError("email", { type: "focus", message: messages }, { shouldFocus: true })
      }
      toast.error(messages)
      captureException(err)
      setLoadingButton(false)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-[110] bg-neutral-70 bg-opacity-50 flex justify-center items-center">
      <div className="bg-white rounded-xl shadow w-[90%] max-w-md lg:max-w-[620px] h-auto px-5 py-5 lg:py-10">
        <form autoComplete="off" onSubmit={handleSubmit(submitForm)}>
          <div className="flex flex-col gap-8 w-full">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <Text customClass="text-sm lg:text-xl" weight="semibold">
                  &#128274; {t("change_account_password")}
                </Text>
                <Text customClass="text-xs lg:text-base">{t("change_account_password_desc")}</Text>
              </div>
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <InputPassword
                    label={`${t("new_password")}`}
                    placeholder={t("enter_your_password")}
                    {...register("password", {
                      required: t("password_required"),
                      pattern: {
                        value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/,
                        message: t("use_8_or_more_characters"),
                      },
                    })}
                    error={!!errors.password}
                    message={
                      watch("password")?.length <= 0 ? errors?.password?.message.toString() : null
                    }
                  />
                  {watch("password")?.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                      <div className="flex gap-x-1 items-center">
                        <Icon
                          iconName={watch("password").length >= 8 ? "check-circle" : "check-close"}
                          width={16}
                          color={watch("password").length >= 8 ? tokens.secondary.DEFAULT : "red"}
                        />
                        <Text size="small" color="gray">
                          {t("8_characters")}
                        </Text>
                      </div>
                      <div className="flex gap-x-1 items-center">
                        <Icon
                          iconName={
                            hasUppercase(watch("password")) ? "check-circle" : "check-close"
                          }
                          width={16}
                          color={hasUppercase(watch("password")) ? tokens.secondary.DEFAULT : "red"}
                        />
                        <Text size="small" color="gray">
                          {t("use_one_uppercase")}
                        </Text>
                      </div>
                      <div className="flex gap-x-1 items-center">
                        <Icon
                          iconName={
                            hasLowercase(watch("password")) ? "check-circle" : "check-close"
                          }
                          width={16}
                          color={hasLowercase(watch("password")) ? tokens.secondary.DEFAULT : "red"}
                        />
                        <Text size="small" color="gray">
                          {t("use_one_lowercase")}
                        </Text>
                      </div>
                      <div className="flex gap-x-1 items-center">
                        <Icon
                          iconName={
                            containsNumbers(watch("password")) ? "check-circle" : "check-close"
                          }
                          width={16}
                          color={containsNumbers(watch("password")) ? tokens.secondary.DEFAULT : "red"}
                        />
                        <Text size="small" color="gray">
                          {t("use_one_number")}
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
                <InputPassword
                  label={t("confirm_password")}
                  placeholder={t("enter_password_confirmation")}
                  {...register("confirmPassword", {
                    required: t("confirm_password_required"),
                  })}
                  success={
                    watch("password") &&
                    watch("password").length > 0 &&
                    watch("confirmPassword") === watch("password")
                  }
                  successMessage={t("password_confirmation_matches")}
                  error={!!errors.confirmPassword}
                  message={errors.confirmPassword && errors.confirmPassword.message.toString()}
                />
              </div>
            </div>
            <Button
              variant="contained"
              color={!watch("password") || !watch("confirmPassword") || watch("password") !== watch("confirmPassword") ? "gray" : "primary"}
              fullWidth
              type="submit"
              loading={loadingButton}
              disabled={
                !userProfile || loadingButton || !watch("password") || !watch("confirmPassword") || watch("password") !== watch("confirmPassword")
              }>
              {t("save")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ModalChangeAccountPassword
